import React, { useState, useEffect } from 'react';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useQuery } from '@tanstack/react-query';
import api, { errMsg, API } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import { IntegrationPanel } from '@/components/IntegrationPanel';
import { CategoryPrefixPanel } from '@/components/CategoryPrefixPanel';
import { AppearancePanel } from '@/components/AppearancePanel';
import { SplashBrandingPanel } from '@/components/SplashBrandingPanel';
import { ReceiptCustomizerPanel } from '@/components/ReceiptCustomizerPanel';
import { CustomFieldsPanel } from '@/components/CustomFieldsPanel';
import { BillingOptionsPanel } from '@/components/BillingOptionsPanel';
import { InstallAppButton } from '@/components/InstallAppButton';
import { AutoUpdatePanel } from '@/components/AutoUpdatePanel';
import { ScanSoundPanel } from '@/components/ScanSoundPanel';
import { AppLogoPanel } from '@/components/AppLogoPanel';
import { SecurityPanel } from '@/components/SecurityPanel';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Store as StoreIcon, Trash2, Upload, QrCode, CreditCard, LayoutDashboard, GripVertical, Tag, Pencil, Database } from 'lucide-react';

export default function Settings() {
  const { stores, loadStores, loadSettings, settings, user, can } = useApp();
  const { data } = useQuery({ queryKey: ['settings'], queryFn: async () => (await api.get('/settings')).data.data });
  const [form, setForm] = useState({ business_name: '', address: '', phone: '', footer_note: '' });
  const [storeNames, setStoreNames] = useState({});
  const [newStore, setNewStore] = useState('');
  const [payAccts, setPayAccts] = useState({ UPI: [], CARD: [] });
  const [dash, setDash] = useState({ layout: 'grid2', tiles: [] });
  useEffect(() => { if (data) setForm({ business_name: data.business_name || '', address: data.address || '', phone: data.phone || '', footer_note: data.footer_note || '' }); }, [data]);
  useEffect(() => { setStoreNames(Object.fromEntries(stores.map((s) => [s.id, s.name]))); }, [stores]);
  useEffect(() => { if (settings?.payment_accounts) setPayAccts(JSON.parse(JSON.stringify(settings.payment_accounts))); if (settings?.dashboard) setDash(JSON.parse(JSON.stringify(settings.dashboard))); }, [settings]);

  const savePayAccts = async (next) => {
    setPayAccts(next);
    try { await api.put('/settings', { payment_accounts: next }); await loadSettings(); toast.success('Payment settings saved'); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const updUpi = (id, field, val) => setPayAccts((p) => ({ ...p, UPI: p.UPI.map((a) => a.id === id ? { ...a, [field]: val } : a) }));
  const addUpi = () => setPayAccts((p) => ({ ...p, UPI: [...p.UPI, { id: 'upi-' + Date.now(), name: 'New UPI', upi_id: '', qr_image: '' }] }));
  const delUpi = (id) => savePayAccts({ ...payAccts, UPI: payAccts.UPI.filter((a) => a.id !== id) });
  const uploadQr = (id, file) => {
    if (!file) return;
    if (file.size > 600000) { toast.error('QR image too large (max ~600KB). Use a smaller image.'); return; }
    const reader = new FileReader();
    reader.onload = () => { const next = { ...payAccts, UPI: payAccts.UPI.map((a) => a.id === id ? { ...a, qr_image: reader.result } : a) }; savePayAccts(next); };
    reader.readAsDataURL(file);
  };
  const updCard = (id, val) => setPayAccts((p) => ({ ...p, CARD: p.CARD.map((a) => a.id === id ? { ...a, name: val } : a) }));
  const addCard = () => setPayAccts((p) => ({ ...p, CARD: [...p.CARD, { id: 'card-' + Date.now(), name: 'New Card' }] }));
  const delCard = (id) => savePayAccts({ ...payAccts, CARD: payAccts.CARD.filter((a) => a.id !== id) });

  const saveDash = async (next) => {
    setDash(next);
    try { await api.put('/settings', { dashboard: next }); await loadSettings(); toast.success('Dashboard updated'); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggleTile = (key) => saveDash({ ...dash, tiles: dash.tiles.map((t) => t.key === key ? { ...t, enabled: !t.enabled } : t) });
  const moveTile = (idx, dir) => {
    const tiles = [...dash.tiles]; const j = idx + dir;
    if (j < 0 || j >= tiles.length) return;
    [tiles[idx], tiles[j]] = [tiles[j], tiles[idx]];
    saveDash({ ...dash, tiles });
  };
  const LAYOUTS = [{ key: 'grid2', label: '2 columns' }, { key: 'grid1', label: 'List' }, { key: 'large', label: 'Large tiles' }];
  const [dragIdx, setDragIdx] = useState(null);
  const dropTile = (toIdx) => {
    if (dragIdx === null || dragIdx === toIdx) { setDragIdx(null); return; }
    const tiles = [...dash.tiles];
    const [moved] = tiles.splice(dragIdx, 1);
    tiles.splice(toIdx, 0, moved);
    setDragIdx(null);
    saveDash({ ...dash, tiles });
  };


  const renameStore = async (id) => {
    const name = (storeNames[id] || '').trim();
    if (!name) { toast.error('Store name cannot be empty.'); return; }
    try { await api.patch(`/stores/${id}`, { name }); await loadStores(); toast.success('Store name updated'); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const addStore = async () => {
    if (!newStore.trim()) { toast.error('Enter a store/branch name.'); return; }
    try { await api.post('/stores', { name: newStore.trim() }); await loadStores(); setNewStore(''); toast.success('Store added'); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const [delStore, setDelStore] = useState(null);
  const deleteStore = async (id) => {
    try { await api.delete(`/stores/${id}`); await loadStores(); setDelStore(null); toast.success('Store deleted'); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const [purgeStore, setPurgeStore] = useState(null); // store object
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [purgeReason, setPurgeReason] = useState('');
  const [purging, setPurging] = useState(false);
  const doPurge = async () => {
    if (!purgeStore) return;
    setPurging(true);
    try {
      const r = await api.post(`/stores/${purgeStore.id}/purge`, { confirm: purgeConfirm.trim(), reason: purgeReason.trim() });
      toast.success(`Deleted ${r.data.data.deleted_total} record(s) for ${r.data.data.name}`);
      setPurgeStore(null); setPurgeConfirm(''); setPurgeReason('');
      await loadStores();
    } catch (e) { toast.error(errMsg(e)); } finally { setPurging(false); }
  };

  const save = async () => {
    try { await api.put('/settings', form); toast.success('Settings saved'); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Page>
      <TopBar title="Settings" showStore={false} />
      <div className="px-4 pt-4 space-y-4">
        <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
          <p className="text-sm font-semibold">Business</p>
          <div><Label className="text-xs">Business name</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="settings-name" /></div>
          <div><Label className="text-xs">Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
          <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
          <div><Label className="text-xs">Invoice footer note</Label><Input value={form.footer_note} onChange={(e) => setForm({ ...form, footer_note: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
          <Button className="h-11 w-full rounded-xl" onClick={save} data-testid="settings-save">Save settings</Button>
        </div>
        <InstallAppButton />
        <AutoUpdatePanel />
        <ScanSoundPanel />
        <SecurityPanel />
        {(user?.role === 'OWNER' || user?.owner_access) && <AppLogoPanel />}
        {(user?.role === 'OWNER' || user?.owner_access) && <AppearancePanel />}
        {(user?.role === 'OWNER' || user?.owner_access) && <ReceiptCustomizerPanel />}
        {(user?.role === 'OWNER' || user?.owner_access) && <BillingOptionsPanel />}
        {(user?.role === 'OWNER' || user?.owner_access) && <CustomFieldsPanel />}
        {(user?.role === 'OWNER' || user?.owner_access) && <SplashBrandingPanel />}
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="mb-1 flex items-center gap-2"><StoreIcon className="h-4 w-4" /><p className="text-sm font-semibold">Stores &amp; branches</p></div>
          <p className="mb-3 text-xs text-muted-foreground">Rename your stores or add another branch/business. Names update everywhere instantly.</p>
          <div className="space-y-2">
            {stores.map((s) => (
              <div key={s.id} className="flex items-center gap-2" data-testid={`settings-store-${s.id}`}>
                <Input value={storeNames[s.id] ?? ''} onChange={(e) => setStoreNames((n) => ({ ...n, [s.id]: e.target.value }))} className="h-11 rounded-xl" data-testid={`settings-store-name-${s.id}`} />
                <Button variant="secondary" className="h-11 rounded-xl" onClick={() => renameStore(s.id)} data-testid={`settings-store-save-${s.id}`}>Save</Button>
                {(user?.role === 'OWNER' || user?.owner_access) && (
                  <button onClick={() => { setPurgeStore(s); setPurgeConfirm(''); setPurgeReason(''); }} title="Delete all data for this store" data-testid={`settings-store-purge-${s.id}`}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-amber-600 hover:bg-amber-500/10"><Database className="h-4 w-4" /></button>
                )}
                {(user?.role === 'OWNER' || user?.owner_access) && (
                  delStore === s.id ? (
                    <>
                      <Button variant="destructive" className="h-11 rounded-xl" onClick={() => deleteStore(s.id)} data-testid={`settings-store-del-confirm-${s.id}`}>Delete</Button>
                      <Button variant="ghost" className="h-11 rounded-xl" onClick={() => setDelStore(null)}>Cancel</Button>
                    </>
                  ) : (
                    <button onClick={() => setDelStore(s.id)} title="Delete store" data-testid={`settings-store-del-${s.id}`}
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
                  )
                )}
              </div>
            ))}
          </div>
          {delStore && <p className="mt-2 text-xs text-muted-foreground">A store can only be deleted when it has no linked garments, sales, purchases, expenses or business data. Use the amber database icon to wipe a store's data first.</p>}
          <div className="mt-3 flex gap-2">
            <Input value={newStore} onChange={(e) => setNewStore(e.target.value)} placeholder="Add new store / branch" className="h-11 rounded-xl" data-testid="settings-new-store" />
            <Button className="h-11 rounded-xl" onClick={addStore} data-testid="settings-add-store"><Plus className="mr-1 h-4 w-4" />Add</Button>
          </div>
        </div>
        <ExpenseCategoryManager />
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="mb-1 flex items-center gap-2"><QrCode className="h-4 w-4" /><p className="text-sm font-semibold">Payment settings — UPI accounts</p></div>
          <p className="mb-3 text-xs text-muted-foreground">Add your bank UPI accounts and their QR. Staff pick the account at checkout and its QR shows for the customer.</p>
          <div className="mb-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-800">
            <span>💡</span>
            <p>If a scanned QR shows <b>"PSP not registered"</b>, the UPI ID isn't a valid handle. Use a real VPA like <b>name@okhdfcbank</b> / <b>name@okaxis</b>, or simply <b>upload the QR image</b> from your PhonePe/GPay/bank app below — that always works.</p>
          </div>
          <div className="space-y-3">
            {payAccts.UPI?.map((a) => (
              <div key={a.id} className="rounded-xl border border-border p-3" data-testid={`payset-upi-${a.id}`}>
                <div className="flex items-center gap-2">
                  <Input value={a.name} onChange={(e) => updUpi(a.id, 'name', e.target.value)} placeholder="Bank name" className="h-10 rounded-lg text-sm font-medium" data-testid={`payset-upi-name-${a.id}`} />
                  <button onClick={() => delUpi(a.id)} className="flex h-10 w-10 items-center justify-center rounded-lg text-destructive" data-testid={`payset-upi-del-${a.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
                <Input value={a.upi_id || ''} onChange={(e) => updUpi(a.id, 'upi_id', e.target.value)} placeholder="UPI ID (e.g. shabz@okicici) — auto-generates QR" className="mt-2 h-10 rounded-lg text-sm" data-testid={`payset-upi-id-${a.id}`} />
                <div className="mt-2 flex items-center gap-2">
                  <label className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-border px-3 py-2 text-xs font-medium">
                    <Upload className="h-3 w-3" /> Upload QR image
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadQr(a.id, e.target.files?.[0])} data-testid={`payset-upi-qr-${a.id}`} />
                  </label>
                  {a.qr_image && <img src={a.qr_image} alt="QR" className="h-12 w-12 rounded object-contain ring-1 ring-border" />}
                  {a.qr_image && <button onClick={() => savePayAccts({ ...payAccts, UPI: payAccts.UPI.map((x) => x.id === a.id ? { ...x, qr_image: '' } : x) })} className="text-xs text-destructive underline">remove QR</button>}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <Button variant="secondary" className="h-10 flex-1 rounded-xl" onClick={addUpi} data-testid="payset-add-upi"><Plus className="mr-1 h-4 w-4" />Add UPI</Button>
            <Button className="h-10 flex-1 rounded-xl" onClick={() => savePayAccts(payAccts)} data-testid="payset-save-upi">Save UPI accounts</Button>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="mb-1 flex items-center gap-2"><CreditCard className="h-4 w-4" /><p className="text-sm font-semibold">Payment settings — Card machines</p></div>
          <div className="space-y-2">
            {payAccts.CARD?.map((a) => (
              <div key={a.id} className="flex items-center gap-2" data-testid={`payset-card-${a.id}`}>
                <Input value={a.name} onChange={(e) => updCard(a.id, e.target.value)} className="h-10 rounded-lg text-sm" data-testid={`payset-card-name-${a.id}`} />
                <button onClick={() => delCard(a.id)} className="flex h-10 w-10 items-center justify-center rounded-lg text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <Button variant="secondary" className="h-10 flex-1 rounded-xl" onClick={addCard} data-testid="payset-add-card"><Plus className="mr-1 h-4 w-4" />Add card machine</Button>
            <Button className="h-10 flex-1 rounded-xl" onClick={() => savePayAccts(payAccts)} data-testid="payset-save-card">Save card machines</Button>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="mb-1 flex items-center gap-2"><LayoutDashboard className="h-4 w-4" /><p className="text-sm font-semibold">Owner dashboard — tiles &amp; layout</p></div>
          <p className="mb-3 text-xs text-muted-foreground">Choose which cards show on the owner Home, drag to reorder, and pick a layout.</p>
          <div className="mb-3 flex flex-wrap gap-2">
            {LAYOUTS.map((l) => (
              <button key={l.key} data-testid={`dash-layout-${l.key}`} onClick={() => saveDash({ ...dash, layout: l.key })}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium ${dash.layout === l.key ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'}`}>{l.label}</button>
            ))}
          </div>
          <div className="space-y-2">
            {dash.tiles?.map((t, idx) => (
              <div key={t.key} draggable onDragStart={() => setDragIdx(idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => dropTile(idx)}
                className={`flex items-center justify-between rounded-xl border p-2.5 ${dragIdx === idx ? 'border-primary opacity-60' : 'border-border'}`} data-testid={`dash-tile-${t.key}`}>
                <div className="flex items-center gap-2">
                  <GripVertical className="h-4 w-4 cursor-grab text-muted-foreground" data-testid={`dash-tile-grip-${t.key}`} />
                  <input type="checkbox" checked={t.enabled} onChange={() => toggleTile(t.key)} className="h-4 w-4 accent-[hsl(var(--primary))]" data-testid={`dash-tile-toggle-${t.key}`} />
                  <span className="text-sm">{t.label}</span>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => moveTile(idx, -1)} className="rounded-md border border-border px-2 py-1 text-xs" data-testid={`dash-tile-up-${t.key}`}>↑</button>
                  <button onClick={() => moveTile(idx, 1)} className="rounded-md border border-border px-2 py-1 text-xs" data-testid={`dash-tile-down-${t.key}`}>↓</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {(user?.role === 'OWNER' || user?.owner_access || (can && can('stock_add'))) && <CategoryPrefixPanel />}
        {(user?.role === 'OWNER' || user?.owner_access) && <IntegrationPanel />}
      </div>

      <AlertDialog open={!!purgeStore} onOpenChange={(o) => { if (!o) setPurgeStore(null); }}>
        <AlertDialogContent data-testid="store-purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Delete ALL data for "{purgeStore?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes every sale, inventory item, purchase, expense, incentive,
              return, transfer and business-data record for this store. The store itself is kept so
              you can reuse or delete it afterwards. This <b>cannot be undone</b> and is recorded in the audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-1">
            <div>
              <Label className="text-xs">Type the store name to confirm</Label>
              <Input className="mt-1 h-11 rounded-xl" value={purgeConfirm} placeholder={purgeStore?.name}
                onChange={(e) => setPurgeConfirm(e.target.value)} data-testid="store-purge-confirm" />
            </div>
            <div>
              <Label className="text-xs">Reason (required)</Label>
              <Input className="mt-1 h-11 rounded-xl" value={purgeReason} placeholder="e.g. closing this branch"
                onChange={(e) => setPurgeReason(e.target.value)} data-testid="store-purge-reason" />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={purging}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doPurge}
              disabled={purging || purgeConfirm.trim().toLowerCase() !== (purgeStore?.name || '').trim().toLowerCase() || !purgeReason.trim()}
              data-testid="store-purge-execute"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {purging ? 'Deleting…' : 'Delete all data'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}


function ExpenseCategoryManager() {
  const [cats, setCats] = useState([]);
  const [newCat, setNewCat] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [delId, setDelId] = useState(null);
  const load = async () => {
    try { setCats((await api.get('/expense-categories')).data.data || []); } catch (e) { console.error('Failed to load expense categories', e); }
  };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!newCat.trim()) return toast.error('Enter a category name.');
    try { await api.post('/expense-categories', { name: newCat.trim() }); setNewCat(''); await load(); toast.success('Category added'); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const rename = async (id) => {
    if (!editName.trim()) return;
    try { await api.put(`/expense-categories/${id}`, { name: editName.trim() }); setEditId(null); await load(); toast.success('Category updated'); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/expense-categories/${id}`); setDelId(null); await load(); toast.success('Category deleted'); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="expense-categories-manager">
      <div className="mb-1 flex items-center gap-2"><Tag className="h-4 w-4" /><p className="text-sm font-semibold">Expense categories</p></div>
      <p className="mb-3 text-xs text-muted-foreground">Add, rename or remove expense categories used in the expense forms. Removing a category does not change past expenses.</p>
      <div className="space-y-2">
        {cats.map((c) => (
          <div key={c.id} className="flex items-center gap-2" data-testid={`exp-cat-${c.id}`}>
            {editId === c.id ? (
              <>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-11 rounded-xl" data-testid={`exp-cat-edit-${c.id}`} />
                <Button variant="secondary" className="h-11 rounded-xl" onClick={() => rename(c.id)} data-testid={`exp-cat-save-${c.id}`}>Save</Button>
                <Button variant="ghost" className="h-11 rounded-xl" onClick={() => setEditId(null)}>Cancel</Button>
              </>
            ) : delId === c.id ? (
              <>
                <span className="flex-1 text-sm">{c.name}</span>
                <Button variant="destructive" className="h-11 rounded-xl" onClick={() => del(c.id)} data-testid={`exp-cat-del-confirm-${c.id}`}>Delete</Button>
                <Button variant="ghost" className="h-11 rounded-xl" onClick={() => setDelId(null)}>Cancel</Button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm">{c.name}</span>
                <button onClick={() => { setEditId(c.id); setEditName(c.name); }} title="Rename" data-testid={`exp-cat-rename-${c.id}`}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-foreground hover:bg-white/5"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => setDelId(c.id)} title="Delete" data-testid={`exp-cat-delbtn-${c.id}`}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
              </>
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Add expense category" className="h-11 rounded-xl" data-testid="exp-cat-new" />
        <Button className="h-11 rounded-xl" onClick={add} data-testid="exp-cat-add"><Plus className="mr-1 h-4 w-4" />Add</Button>
      </div>
    </div>
  );
}
